import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MdExpandMore,
  MdSearch,
  MdEdit,
  MdDelete,
  MdArrowUpward,
  MdArrowDownward,
} from "react-icons/md";
import styled from "styled-components/macro";
import { TYPED_OPERATOR_LABELS } from "./helpers/filterOperators";

// Reusable, opt-in column dropdown for <DataTable> (AC-15377 / reused by
// AC-15498). Rendered only for columns that set `enableColumnMenu: true`.
// Section order matches FR-1: search → active-filter status → sort → filter
// submenu (passed in via children, e.g. <TextColumnFilter>). Closes on
// outside-click, Escape, and on scroll/resize of the ancestor.
//
// Filter value shape (per column): { operator: string, values: string[] }.
// The search input is a shortcut that sets a debounced live `contains` filter;
// the explicit operator submenu (children) writes the same column filter slot.
//
// NOTE for AC-15498 client-side reuse: in client mode (manualFilters: false)
// react-table runs the column's `filter` fn over this object value — the
// default text filter stringifies it. CLIENT-bucket tables must supply a
// custom `column.filter` that understands the { operator, values } shape.

const SEARCH_DEBOUNCE_MS = 300;

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const ChevronButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 0;
  color: rgba(0, 0, 0, 0.6);
`;

// Portaled to document.body (fixed positioning) so the panel is never clipped
// by the table's overflow:auto scroll container.
const Panel = styled.div`
  position: fixed;
  z-index: 9999;
  min-width: 240px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  padding: 0.25rem 0.5rem;
  text-align: left;
  font-weight: 400;
  cursor: auto;
`;

const Section = styled.div`
  padding: 0.4rem 0.1rem;
  & + & {
    border-top: 1px solid #f0f0f0;
  }
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.25rem 0.4rem;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-size: 0.85rem;
`;

const ActiveFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: #eff6ff;
  border-radius: 4px;
  padding: 0.35rem 0.4rem;
  font-size: 0.8rem;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.6);
  padding: 0;
  &:hover {
    color: #2563eb;
  }
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  background: ${(p) => (p.active ? "#eff6ff" : "none")};
  border: none;
  cursor: pointer;
  padding: 0.3rem 0.4rem;
  font-size: 0.85rem;
  text-align: left;
  color: ${(p) => (p.active ? "#2563eb" : "inherit")};
  &:hover {
    background: #f5f5f5;
  }
`;

export const OPERATOR_LABELS = {
  eq: "Equals",
  neq: "Does Not Equal",
  contains: "Contains",
  ncontains: "Does Not Contain",
  empty: "Is Empty",
  nempty: "Is Not Empty",
  in: "In List",
  nin: "Not in List",
  // Numeric/date comparison operators (filterOperators.js).
  ...TYPED_OPERATOR_LABELS,
};

// Operators that carry no value, so the active-filter row offers clear only
// (no edit pencil — there's nothing to re-open).
const VALUELESS_OPERATORS = ["empty", "nempty"];

// Human-readable summary of a column filter value, for the active-filter row.
export function describeFilter(filterValue) {
  if (!filterValue) return "";
  const label = OPERATOR_LABELS[filterValue.operator] ?? filterValue.operator;
  const vals = (filterValue.values ?? []).join(", ");
  return vals ? `${label}: ${vals}` : label;
}

const ColumnMenu = ({ column, sortLabels, children }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  // Bumped by the active-filter row's pencil to tell the filter submenu to
  // re-open its modal prefilled. While that modal is open, suppress the menu's
  // own close handlers so the panel (and thus the modal mounted within it)
  // stays alive.
  const [editToken, setEditToken] = useState(0);
  const [childModalOpen, setChildModalOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const filterValue = column.filterValue;
  const canSort = column.canSort === true;
  // Header is a string for opt-in columns; guard against element headers.
  const headerLabel = typeof column.Header === "string" ? column.Header : "";

  // Position the portaled panel under the chevron, right-aligned to it.
  const positionPanel = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords({
      top: rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  };

  const toggleOpen = (e) => {
    e.stopPropagation();
    setOpen((o) => {
      const next = !o;
      if (next) positionPanel();
      return next;
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      // Don't close while a child value-modal is open (its clicks land outside
      // the panel but must not tear down the panel/modal).
      if (childModalOpen) return;
      // Trigger and panel live in different DOM trees (panel is portaled), so
      // check both before closing.
      if (
        triggerRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !childModalOpen) setOpen(false);
    };
    const close = () => {
      if (!childModalOpen) setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    // Capture phase catches scrolls of the inner table container too.
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, childModalOpen]);

  // The search box mirrors an active `contains` filter; other operators leave
  // it blank (shown in the active-filter row / submenu instead).
  const containsValue = useMemo(
    () =>
      filterValue && filterValue.operator === "contains"
        ? filterValue.values?.[0] ?? ""
        : "",
    [filterValue]
  );
  const [searchText, setSearchText] = useState(containsValue);
  const debounceRef = useRef(null);

  // Re-sync local input when the filter changes externally (cleared via trash,
  // or set via the operator submenu).
  useEffect(() => {
    setSearchText(containsValue);
  }, [containsValue]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const onSearchChange = (e) => {
    const text = e.target.value;
    setSearchText(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      column.setFilter(
        text ? { operator: "contains", values: [text] } : undefined
      );
    }, SEARCH_DEBOUNCE_MS);
  };

  const labels = sortLabels ?? { asc: "Sort A → Z", desc: "Sort Z → A" };

  // Show the active-filter row for any applied filter (including `contains`).
  // The edit pencil only appears for value-bearing operators; Is Empty / Is Not
  // Empty get clear only.
  const showActiveFilterRow = !!filterValue;
  const canEditFilter =
    !!children && filterValue && !VALUELESS_OPERATORS.includes(filterValue.operator);

  return (
    <Wrapper>
      <ChevronButton
        ref={triggerRef}
        type="button"
        className={`col-chevron${open || filterValue ? " is-active" : ""}`}
        aria-label={`${headerLabel} column menu`}
        onClick={toggleOpen}
      >
        <MdExpandMore />
      </ChevronButton>
      {open && coords
        ? createPortal(
            <Panel
              ref={panelRef}
              // While a child value-modal is open the panel must stay mounted
              // (the modal is its child) but shouldn't show behind the modal —
              // the modal portals elsewhere and has no backdrop, so hide the
              // panel visually until the modal closes.
              style={{
                top: coords.top,
                right: coords.right,
                ...(childModalOpen ? { display: "none" } : null),
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Section>
                <SearchRow>
                  <MdSearch />
                  <SearchInput
                    autoFocus
                    placeholder={`Search ${headerLabel}`}
                    value={searchText}
                    onChange={onSearchChange}
                  />
                </SearchRow>
              </Section>

              {showActiveFilterRow ? (
                <Section>
                  <ActiveFilterRow>
                    <span style={{ marginRight: "auto" }}>
                      Filter Applied — {describeFilter(filterValue)}
                    </span>
                    {canEditFilter ? (
                      <IconButton
                        type="button"
                        aria-label="Edit filter"
                        onClick={() => setEditToken((t) => t + 1)}
                      >
                        <MdEdit />
                      </IconButton>
                    ) : null}
                    <IconButton
                      type="button"
                      aria-label="Clear filter"
                      onClick={() => {
                        column.setFilter(undefined);
                        setOpen(false);
                      }}
                    >
                      <MdDelete />
                    </IconButton>
                  </ActiveFilterRow>
                </Section>
              ) : null}

              {canSort ? (
                <Section>
                  {/* Sorting is a terminal action for the menu — dismiss it so
                      the panel doesn't sit over the rows the user just sorted
                      (AC-15841), matching the filter submenu's onClose. */}
                  <SortButton
                    type="button"
                    active={column.isSorted && !column.isSortedDesc}
                    onClick={() => {
                      column.toggleSortBy(false);
                      setOpen(false);
                    }}
                  >
                    <MdArrowUpward /> {labels.asc}
                  </SortButton>
                  <SortButton
                    type="button"
                    active={column.isSorted && column.isSortedDesc}
                    onClick={() => {
                      column.toggleSortBy(true);
                      setOpen(false);
                    }}
                  >
                    <MdArrowDownward /> {labels.desc}
                  </SortButton>
                </Section>
              ) : null}

              {children ? (
                <Section>
                  {React.cloneElement(children, {
                    editToken,
                    onEditingChange: setChildModalOpen,
                    // Let the filter submenu dismiss the whole menu once it
                    // applies/cancels, so the panel doesn't linger.
                    onClose: () => setOpen(false),
                  })}
                </Section>
              ) : null}
            </Panel>,
            document.body
          )
        : null}
    </Wrapper>
  );
};

export default ColumnMenu;
